import { Component, computed, DestroyRef, inject, signal } from '@angular/core';
import { rxResource, takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { catchError, filter, finalize, map, of, switchMap, take, takeUntil, tap } from 'rxjs';
import { MceApiService } from '../../core/mceapi.client.generated';
import { appOpt, getApiErrorMessage } from '../../core/mceapi.client.helpers';
import { isSome } from '../../shared/functions/functional';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule, MatDialogState } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { LeaveSharedBillingDialogComponent } from './leave-shared-billing-dialog.component';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { AuthenticationService } from '../../authentication/authentication.service';
import { hasRole, Role } from '../../shared/enums/role';

@Component({
  selector: 'app-shared-billing',
  imports: [MatCardModule, MatIconModule, MatButtonModule, MatDialogModule, MatProgressSpinnerModule],
  templateUrl: './shared-billing.component.html',
})
export class SharedBillingComponent {
  private readonly authenticationService = inject(AuthenticationService);

  private readonly mceApiService = inject(MceApiService);
  private readonly dialog = inject(MatDialog);
  private readonly destroyRef = inject(DestroyRef);
  private readonly signedInUser$ = toObservable(this.authenticationService.user);

  protected readonly leaveState = signal<'idle' | 'confirming' | 'leaving' | 'waiting'>('idle');
  protected readonly leaveError = signal('');

  protected readonly ownerResource = rxResource({
    stream: () => this.mceApiService.v1SharedBillingOwner().pipe(
      map(dto => {
        const owner = appOpt(dto);
        return isSome(owner) ? owner.value : undefined;
      }),
      catchError(() => of(undefined)),
    ),
  });

  protected readonly isProTier = computed(() =>
    hasRole(this.authenticationService.currentUserRoles(), Role.ProTier));

  protected leaveSharedBilling(): void {
    const uid = this.authenticationService.user()?.uid;
    if (this.leaveState() !== 'idle' || !uid
      || !this.authenticationService.currentUserMatchesSignedInAccount()
      || !hasRole(this.authenticationService.currentUserRoles(), Role.SharedBillingMember)) return;

    this.leaveState.set('confirming');
    this.leaveError.set('');
    const ref = this.dialog.open<LeaveSharedBillingDialogComponent, void, boolean>(LeaveSharedBillingDialogComponent, {
      width: '480px',
      maxWidth: '95vw',
      ariaLabel: 'Leave shared billing?',
      ariaDescribedBy: 'leave-shared-billing-description',
    });
    const sameAccount = () => this.authenticationService.user()?.uid === uid;

    ref.afterClosed().pipe(
      take(1),
      filter(confirmed => confirmed === true && sameAccount()
        && this.authenticationService.currentUserMatchesSignedInAccount()
        && hasRole(this.authenticationService.currentUserRoles(), Role.SharedBillingMember)),
      tap(() => this.leaveState.set('leaving')),
      switchMap(() => this.mceApiService.v1SharedBillingLeave()),
      takeUntil(this.signedInUser$.pipe(filter(user => user?.uid !== uid))),
      takeUntilDestroyed(this.destroyRef),
      finalize(() => {
        if (ref.getState() === MatDialogState.OPEN) ref.close(false);
        if (this.leaveState() !== 'waiting') this.leaveState.set('idle');
      }),
    ).subscribe({
      next: () => {
        if (sameAccount()) this.leaveState.set('waiting');
      },
      error: error => {
        if (sameAccount()) this.leaveError.set(getApiErrorMessage(error));
      },
    });
  }
}
